import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, ChevronDown, ChevronRight, FolderOpen, Share2, Trash2, Play, ClipboardList, Gamepad2, Pencil, Link2, BookOpen, Globe, GraduationCap, SquareStack, Folder as FolderIcon, Star, Network } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Deck, Folder, LANG_FLAGS, LANG_NAMES } from "@/types/linguadeck";
import { AppState } from "@/types/linguadeck";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DeleteDeckModal from "./DeleteDeckModal";
import ShareModal from "./ShareModal";
import ShareLibraryModal from "./ShareLibraryModal";
import { fetchFavoriteCards, removeCardFavorite, type FavoriteCardWithDeck } from "@/lib/db";
import { getLocalizedTestData } from "@/lib/testData";
import type { DeckInitialType } from "./DeckModal";

type LibraryTab = "cards" | "learn" | "test" | "games" | "philology" | "favorites" | "folders";
type SortOption = "recent" | "alphabetical" | "tag" | "language" | "type";
type StudyModeKey = "cards" | "learn" | "test";
type TimeGroup = "today" | "this_week" | "older";

interface HomeViewProps {
  decks: Deck[];
  folders: Folder[];
  loading?: boolean;
  isTeacher: boolean;
  onSelectDeck: (id: string, mode?: "cards" | "learn" | "test" | "games" | "philology") => void;
  /** Fast Track: navigate directly to Fullscreen Swipe Player, bypassing dashboard */
  onStudyDirect?: (deckId: string, mode?: StudyModeKey) => void;
  onNewDeck: (type: DeckInitialType) => void;
  onCreateFolder?: () => void;
  onEditDeck: (id: string) => void;
  onDeleteDeck: (id: string) => void;
  onNavTo: (view: AppState["currentView"]) => void;
  onDeckShared?: (deckId: string, token: string | null, shareAccess?: "read" | "write") => void;
  /** Remove a tag from all decks that contain it (deckType, level, or tags array) */
  onRemoveTag?: (tagName: string) => void | Promise<void>;
}

function getDeckCardCount(deck: Deck): number {
  return deck.cardCount ?? deck.cards?.length ?? 0;
}

function getTimeGroup(iso: string | undefined): TimeGroup {
  if (!iso) return "older";
  const date = new Date(iso);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = (todayStart - dateStart) / (24 * 60 * 60 * 1000);
  if (diffDays === 0) return "today";
  if (diffDays <= 7) return "this_week";
  return "older";
}

/** Resolve studyModes with legacy fallback: undefined → all true (preserve legacy decks in all tabs) */
function getResolvedStudyModes(deck: { studyModes?: { cards?: boolean; learn?: boolean; test?: boolean; match?: boolean; philology?: boolean } }): Record<"cards" | "learn" | "test" | "match" | "philology", boolean> {
  const modes = deck.studyModes;
  if (!modes || typeof modes !== "object") {
    return { cards: true, learn: true, test: true, match: true };
  }
  return { cards: true, learn: true, test: true, match: true, philology: true, ...modes } as Record<"cards" | "learn" | "test" | "match" | "philology", boolean>;
}

type TimeFilterValue = "all_recent" | "today" | "yesterday" | "this_week" | "last_week" | "last_month" | "by_date";

function deckMatchesTimeFilter(
  createdAt: string | undefined,
  filter: TimeFilterValue | null,
  customDateVal: string
): boolean {
  if (!filter || filter === "all_recent") return true;
  if (!createdAt) return false;
  const date = new Date(createdAt);
  const now = new Date();

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.floor((todayStart - dateStart) / (24 * 60 * 60 * 1000));

  switch (filter) {
    case "today":
      return diffDays === 0;
    case "yesterday":
      return diffDays === 1;
    case "this_week":
      return diffDays >= 0 && diffDays <= 6;
    case "last_week": {
      const lastWeekStart = todayStart - 7 * 24 * 60 * 60 * 1000;
      const lastWeekEnd = todayStart - 1;
      return dateStart >= lastWeekStart && dateStart <= lastWeekEnd;
    }
    case "last_month": {
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      return dateStart >= lastMonthStart && dateStart < thisMonthStart;
    }
    case "by_date":
      if (!customDateVal) return false;
      const target = new Date(customDateVal + "T00:00:00").getTime();
      const targetEnd = target + 24 * 60 * 60 * 1000;
      return dateStart >= target && dateStart < targetEnd;
    default:
      return true;
  }
}

export default function HomeView({
  decks,
  folders,
  loading = false,
  isTeacher,
  onSelectDeck,
  onStudyDirect,
  onNewDeck,
  onCreateFolder,
  onEditDeck,
  onDeleteDeck,
  onNavTo,
  onDeckShared,
  onRemoveTag,
}: HomeViewProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const lang = (i18n.language || "en").split("-")[0];
  const focusDeckId = ((location.state as { focusDeckId?: string; scrollToTop?: boolean } | null)?.focusDeckId ?? null);
  const shouldScrollToTop = Boolean((location.state as { focusDeckId?: string; scrollToTop?: boolean } | null)?.scrollToTop);
  const deckRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const handledFocusDeckIdRef = useRef<string | null>(null);
  const [highlightDeckId, setHighlightDeckId] = useState<string | null>(null);

  const deckHasTestData = (deck: Deck) =>
    deck.cards?.length
      ? deck.cards.some((c) => getLocalizedTestData(c, lang))
      : (deck.testableCount ?? 0) > 0;

  const [activeTab, setActiveTab] = useState<LibraryTab>("cards");
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [languageSubmenuOpen, setLanguageSubmenuOpen] = useState(false);
  const [tagSubmenuOpen, setTagSubmenuOpen] = useState(false);
  const [typeSubmenuOpen, setTypeSubmenuOpen] = useState(false);
  const [recentSubmenuOpen, setRecentSubmenuOpen] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [activeTypeFilter, setActiveTypeFilter] = useState<StudyModeKey | null>(null);
  const [activeTimeFilter, setActiveTimeFilter] = useState<string | null>("all_recent");
  const [customDate, setCustomDate] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleLibraryCount, setVisibleLibraryCount] = useState(48);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);

  const LIBRARY_PAGE_SIZE = 48;

  // Close dropdown on outside click (allows submenu to stay open when clicking inside)
  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setSortDropdownOpen(false);
        setLanguageSubmenuOpen(false);
        setTagSubmenuOpen(false);
        setTypeSubmenuOpen(false);
        setRecentSubmenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sortDropdownOpen]);

  // Auto-open native date picker when "By date" mode is activated
  useEffect(() => {
    if (activeTimeFilter === "by_date" && dateInputRef.current) {
      const el = dateInputRef.current;
      const t = setTimeout(() => {
        try {
          el.showPicker?.();
        } catch {
          // showPicker not supported in older browsers
        }
      }, 100);
      return () => clearTimeout(t);
    }
  }, [activeTimeFilter]);

  const [deletingDeck, setDeletingDeck] = useState<Deck | null>(null);
  const [sharingDeck, setSharingDeck] = useState<Deck | null>(null);
  const [deleteTagConfirm, setDeleteTagConfirm] = useState<string | null>(null);
  const [showShareLibraryModal, setShowShareLibraryModal] = useState(false);
  const [favoriteCards, setFavoriteCards] = useState<FavoriteCardWithDeck[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  const loadFavorites = useCallback(async () => {
    if (!user?.id) return;
    setFavoritesLoading(true);
    try {
      const data = await fetchFavoriteCards(user.id);
      setFavoriteCards(data);
    } catch (e) {
      console.warn("[Favorites] Failed to load:", e);
      setFavoriteCards([]);
    } finally {
      setFavoritesLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeTab === "favorites" && user?.id) {
      loadFavorites();
    }
  }, [activeTab, user?.id, loadFavorites]);

  // Filter decks by active tab: study_modes + count threshold (single source: summary counts)
  const tabFilteredDecks = useMemo(() => {
    if (activeTab === "folders" || activeTab === "favorites") return [];
    const hasCount = (deck: Deck, key: "cards" | "learn" | "test" | "games") => {
      const n =
        key === "cards"
          ? deck.cardCount ?? deck.cards?.length ?? 0
          : key === "learn"
            ? deck.learnableCount ?? deck.cardCount ?? deck.cards?.length ?? 0
            : key === "test"
              ? deck.testableCount ?? deck.cardCount ?? deck.cards?.length ?? 0
              : deck.gamesCount ?? deck.cardCount ?? deck.cards?.length ?? 0;
      return n > 0;
    };
    if (activeTab === "games") {
      return decks.filter((deck) => getResolvedStudyModes(deck).match && hasCount(deck, "games"));
    }
    if (activeTab === "philology") {
      return decks.filter((deck) => getResolvedStudyModes(deck).philology && hasCount(deck, "cards"));
    }
    return decks.filter((deck) => {
      const modes = getResolvedStudyModes(deck);
      const modeKey = activeTab as "cards" | "learn" | "test";
      return !!modes[modeKey] && hasCount(deck, modeKey === "cards" ? "cards" : modeKey === "learn" ? "learn" : "test");
    });
  }, [decks, activeTab]);

  // Unique languages from decks (for language filter dropdown)
  const uniqueLanguages = useMemo(() => {
    const seen = new Set<string>();
    const result: { lang: string; emoji: string; label: string }[] = [];
    for (const deck of tabFilteredDecks) {
      const l = deck.lang;
      if (l && !seen.has(l)) {
        seen.add(l);
        result.push({
          lang: l,
          emoji: LANG_FLAGS[l] ?? deck.emoji ?? "📚",
          label: LANG_NAMES[l] ?? `${deck.emoji ?? "📚"} ${l}`,
        });
      }
    }
    result.sort((a, b) => a.label.localeCompare(b.label));
    return result;
  }, [tabFilteredDecks]);

  // Unique tags from decks (deckType, level, and tags array if present)
  const uniqueTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const deck of tabFilteredDecks) {
      if (deck.deckType && deck.deckType.trim()) tagSet.add(deck.deckType.trim());
      if (deck.level && deck.level.trim()) tagSet.add(deck.level.trim());
      const tags = (deck as Deck & { tags?: string[] }).tags;
      if (Array.isArray(tags)) {
        tags.forEach((t) => t && String(t).trim() && tagSet.add(String(t).trim()));
      }
    }
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [tabFilteredDecks]);

  // Type filter (when "По типу" is selected — filter by study mode regardless of tab)
  const typeFilteredDecks = useMemo(() => {
    if (sortBy !== "type" || !activeTypeFilter || activeTab === "folders") return tabFilteredDecks;
    return tabFilteredDecks.filter((deck) => {
      const modes = getResolvedStudyModes(deck);
      return !!modes[activeTypeFilter];
    });
  }, [tabFilteredDecks, sortBy, activeTypeFilter, activeTab]);

  // Apply search (decks, folders, favorites)
  const filteredDecks = useMemo(() => {
    return typeFilteredDecks.filter((deck) => {
      if (activeTab === "folders" || activeTab === "favorites") return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const matchesSearch =
          deck.title.toLowerCase().includes(q) ||
          (deck.level && deck.level.toLowerCase().includes(q)) ||
          (deck.deckType && deck.deckType.toLowerCase().includes(q)) ||
          deck.lang.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      return true;
    });
  }, [typeFilteredDecks, activeTab, searchQuery]);

  const filteredFolders = useMemo(() => {
    if (activeTab !== "folders") return [];
    return folders.filter((folder) => {
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        return folder.title.toLowerCase().includes(q);
      }
      return true;
    });
  }, [folders, activeTab, searchQuery]);

  const filteredFavoriteCards = useMemo(() => {
    if (activeTab !== "favorites") return [];
    if (!searchQuery.trim()) return favoriteCards;
    const q = searchQuery.trim().toLowerCase();
    return favoriteCards.filter(
      (fc) =>
        fc.card.front.toLowerCase().includes(q) ||
        fc.card.back.toLowerCase().includes(q) ||
        fc.deckTitle.toLowerCase().includes(q)
    );
  }, [activeTab, favoriteCards, searchQuery]);

  // Sort
  const sortedDecks = useMemo(() => {
    const arr = [...filteredDecks];
    if (sortBy === "alphabetical") {
      arr.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy === "language") {
      arr.sort((a, b) => {
        const langCompare = (a.lang || "").localeCompare(b.lang || "");
        return langCompare !== 0 ? langCompare : a.title.localeCompare(b.title);
      });
    } else if (sortBy === "tag") {
      arr.sort((a, b) => {
        const tagA = a.deckType || a.level || "";
        const tagB = b.deckType || b.level || "";
        const tagCompare = tagA.localeCompare(tagB);
        return tagCompare !== 0 ? tagCompare : a.title.localeCompare(b.title);
      });
    } else {
      arr.sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
    }
    return arr;
  }, [filteredDecks, sortBy]);

  const sortedFolders = useMemo(() => {
    const arr = [...filteredFolders];
    if (sortBy === "alphabetical") {
      arr.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy === "tag") {
      arr.sort((a, b) => {
        const tagA = (a.tags?.[0] ?? "").toLowerCase();
        const tagB = (b.tags?.[0] ?? "").toLowerCase();
        const tagCompare = tagA.localeCompare(tagB);
        return tagCompare !== 0 ? tagCompare : a.title.localeCompare(b.title);
      });
    } else {
      arr.sort((a, b) => {
        const da = new Date(a.created_at).getTime();
        const db = new Date(b.created_at).getTime();
        return db - da;
      });
    }
    return arr;
  }, [filteredFolders, sortBy]);

  // Language filter (when sortBy === 'language' and a chip is selected)
  const languageFilteredDecks = useMemo(() => {
    if (sortBy !== "language" || activeLanguage === null) return sortedDecks;
    return sortedDecks.filter((deck) => deck.lang === activeLanguage);
  }, [sortedDecks, sortBy, activeLanguage]);

  // Tag filter (when sortBy === 'tag' and a tag is selected)
  const tagFilteredDecks = useMemo(() => {
    if (sortBy !== "tag" || activeTag === null) return languageFilteredDecks;
    return languageFilteredDecks.filter((deck) => {
      if (deck.deckType === activeTag || deck.level === activeTag) return true;
      const tags = (deck as Deck & { tags?: string[] }).tags;
      return Array.isArray(tags) && tags.some((t) => String(t).trim() === activeTag);
    });
  }, [languageFilteredDecks, sortBy, activeTag]);

  // Time filter (when sortBy === 'recent' and a time filter is selected)
  const timeFilteredDecks = useMemo(() => {
    if (sortBy !== "recent") return tagFilteredDecks;
    return tagFilteredDecks.filter((deck) =>
      deckMatchesTimeFilter(deck.created_at, activeTimeFilter as TimeFilterValue | null, customDate)
    );
  }, [tagFilteredDecks, sortBy, activeTimeFilter, customDate]);

  const timeFilteredFolders = useMemo(() => {
    if (sortBy !== "recent") return sortedFolders;
    return sortedFolders.filter((folder) =>
      deckMatchesTimeFilter(folder.created_at, activeTimeFilter as TimeFilterValue | null, customDate)
    );
  }, [sortedFolders, sortBy, activeTimeFilter, customDate]);

  // Group by time (uses time-filtered decks/folders when sortBy is recent)
  const groupedDecks = useMemo(() => {
    const groups: Record<TimeGroup, Deck[]> = { today: [], this_week: [], older: [] };
    const source = sortBy === "recent" ? timeFilteredDecks : tagFilteredDecks;
    for (const deck of source) {
      const g = getTimeGroup(deck.created_at);
      groups[g].push(deck);
    }
    return groups;
  }, [tagFilteredDecks, timeFilteredDecks, sortBy]);

  const groupedFolders = useMemo(() => {
    const groups: Record<TimeGroup, Folder[]> = { today: [], this_week: [], older: [] };
    const source = sortBy === "recent" ? timeFilteredFolders : sortedFolders;
    for (const folder of source) {
      const g = getTimeGroup(folder.created_at);
      groups[g].push(folder);
    }
    return groups;
  }, [sortedFolders, timeFilteredFolders, sortBy]);

  const totalLibraryItemsCount = useMemo(() => {
    const order: TimeGroup[] = ["today", "this_week", "older"];
    let n = 0;
    if (activeTab !== "folders" && activeTab !== "favorites") {
      for (const k of order) n += (groupedDecks[k]?.length ?? 0);
    }
    if (activeTab === "folders") {
      for (const k of order) n += (groupedFolders[k]?.length ?? 0);
    }
    return n;
  }, [activeTab, groupedDecks, groupedFolders]);

  const showDecks = activeTab !== "folders" && activeTab !== "favorites";
  const showFolders = activeTab === "folders";
  const showFavorites = activeTab === "favorites";
  const totalDecks = decks.length;
  const totalFolders = folders.length;
  const showLibraryLoading = loading && !showFavorites && totalDecks === 0 && totalFolders === 0;
  const hasItems = showFavorites
    ? filteredFavoriteCards.length > 0
    : showDecks
      ? tagFilteredDecks.length > 0
      : sortedFolders.length > 0;

  useEffect(() => {
    setVisibleLibraryCount(LIBRARY_PAGE_SIZE);
  }, [activeTab, sortBy, searchQuery, activeLanguage, activeTag, activeTypeFilter, activeTimeFilter]);

  const hasMoreLibraryItems = visibleLibraryCount < totalLibraryItemsCount;
  useEffect(() => {
    if (!hasMoreLibraryItems || activeTab === "favorites") return;
    const rafId = requestAnimationFrame(() => {
      const sentinel = loadMoreSentinelRef.current;
      if (!sentinel) return;
      loadMoreObserverRef.current?.disconnect();
      let requested = false;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting || requested) return;
          requested = true;
          setVisibleLibraryCount((c) => c + LIBRARY_PAGE_SIZE);
        },
        { root: null, rootMargin: "0px 0px 320px 0px", threshold: 0.01 }
      );
      loadMoreObserverRef.current = observer;
      observer.observe(sentinel);
    });
    return () => {
      cancelAnimationFrame(rafId);
      loadMoreObserverRef.current?.disconnect();
      loadMoreObserverRef.current = null;
    };
  }, [hasMoreLibraryItems, activeTab, visibleLibraryCount, totalLibraryItemsCount]);

  const getDeckStudyMode = (): "cards" | "learn" | "test" | "games" | "philology" | undefined => {
    if (activeTab === "folders" || activeTab === "favorites") return undefined;
    return activeTab;
  };

  const getFilterButtonLabel = (): string => {
    if (sortBy === "type" && activeTypeFilter) {
      const keys: Record<StudyModeKey, string> = {
        cards: t("deckModal.studyModeCards"),
        learn: t("deckModal.studyModeLearn"),
        test: t("deckModal.studyModeTest"),
      };
      return keys[activeTypeFilter];
    }
    if (activeLanguage) return LANG_NAMES[activeLanguage] ?? activeLanguage;
    if (activeTag) return `#${activeTag}`;
    if (sortBy === "recent" && activeTimeFilter && activeTimeFilter !== "all_recent") {
      if (activeTimeFilter === "by_date") return t("home.timeFilterByDate");
      const labels: Record<string, string> = {
        today: t("home.groupToday"),
        yesterday: t("home.timeFilterYesterday"),
        this_week: t("home.groupThisWeek"),
        last_week: t("home.timeFilterLastWeek"),
        last_month: t("home.timeFilterLastMonth"),
        by_date: t("home.timeFilterByDate"),
      };
      return labels[activeTimeFilter] ?? t("home.sortRecent");
    }
    if (sortBy === "recent") return t("home.sortRecent");
    if (sortBy === "type") return t("home.sortByType");
    return t("home.filter");
  };
  const handleConfirmDelete = () => {
    if (deletingDeck) {
      onDeleteDeck(deletingDeck.id);
      setDeletingDeck(null);
    }
  };

  const handleShare = (deck: Deck) => {
    setSharingDeck(deck);
  };

  const handleDeleteTag = (e: React.MouseEvent, tagName: string) => {
    e.stopPropagation();
    e.preventDefault();
    setDeleteTagConfirm(tagName);
  };

  const handleConfirmDeleteTag = async () => {
    const tagName = deleteTagConfirm;
    if (!tagName) return;
    setDeleteTagConfirm(null);
    if (activeTag === tagName) {
      setActiveTag(null);
    }
    await onRemoveTag?.(tagName);
  };

  const tabs: { id: LibraryTab; labelKey: string; icon: React.ReactNode }[] = [
    { id: "cards", labelKey: "deckModal.studyModeCards", icon: <SquareStack className="w-4 h-4" /> },
    { id: "learn", labelKey: "deckModal.studyModeLearn", icon: <GraduationCap className="w-4 h-4" /> },
    { id: "test", labelKey: "deckModal.studyModeTest", icon: <ClipboardList className="w-4 h-4" /> },
    { id: "games", labelKey: "home.tabsGames", icon: <Gamepad2 className="w-4 h-4" /> },
    { id: "philology", labelKey: "deckModal.studyModePhilology", icon: <Network className="w-4 h-4" /> },
    { id: "favorites", labelKey: "favorites", icon: <Star className="w-4 h-4" /> },
    { id: "folders", labelKey: "home.tabsFolders", icon: <FolderIcon className="w-4 h-4" /> },
  ];

  const groupOrder: TimeGroup[] = ["today", "this_week", "older"];
  const groupLabels: Record<TimeGroup, string> = {
    today: t("home.groupToday"),
    this_week: t("home.groupThisWeek"),
    older: t("home.groupOlder"),
  };

  const btnPrimary = "inline-flex items-center justify-center gap-2 h-11 px-4 md:px-5 text-sm font-semibold rounded-xl md:rounded-2xl shadow-sm transition-all duration-200 ease-out border-0 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] text-white hover:shadow-md hover:opacity-95 hover:scale-[1.02] active:scale-[0.98]";
  const btnSecondary = "inline-flex items-center justify-center gap-2 h-10 md:h-11 px-4 md:px-5 text-sm font-medium rounded-xl md:rounded-2xl transition-all duration-150 border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))] hover:shadow-md hover:bg-[hsl(var(--bg3))]/80 hover:text-[hsl(var(--foreground))] active:scale-[0.98]";
  const btnDanger = "inline-flex items-center justify-center gap-2 h-10 md:h-11 px-4 md:px-5 text-sm font-medium rounded-xl md:rounded-2xl transition-all duration-150 border border-[rgba(255,90,138,.2)] bg-[rgba(255,90,138,.04)] text-[hsl(var(--muted-foreground))] hover:bg-[rgba(255,90,138,.1)] hover:border-[rgba(255,90,138,.35)] hover:text-[hsl(var(--accent3))] active:scale-[0.98]";
  const btnGames = "inline-flex items-center justify-center gap-2 h-11 px-4 md:px-5 text-sm font-semibold rounded-xl md:rounded-2xl shadow-sm transition-all duration-200 ease-out border-0 bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500 text-white border-transparent hover:shadow-md hover:scale-[1.02] active:scale-[0.98]";

  const showLearnButton = activeTab !== "games" && activeTab !== "folders" && activeTab !== "favorites";
  const showGamesButton = activeTab === "games";

  useEffect(() => {
    if (!shouldScrollToTop) return;
    const frame = window.requestAnimationFrame(() => {
      const mainScroller = document.querySelector("main");
      if (mainScroller instanceof HTMLElement) {
        mainScroller.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [shouldScrollToTop]);

  useEffect(() => {
    if (!focusDeckId || handledFocusDeckIdRef.current === focusDeckId) return;
    const focusDeckExists = decks.some((deck) => deck.id === focusDeckId);
    if (!focusDeckExists) return;
    handledFocusDeckIdRef.current = focusDeckId;
    setHighlightDeckId(focusDeckId);
    const frame = window.requestAnimationFrame(() => {
      deckRefs.current[focusDeckId]?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    });
    const timer = window.setTimeout(() => {
      setHighlightDeckId((current) => (current === focusDeckId ? null : current));
    }, 2600);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [decks, focusDeckId]);

  const renderDeckCard = (deck: Deck) => {
    const isHighlighted = highlightDeckId === deck.id;
    const restingBorderColor = isHighlighted ? "hsl(var(--primary))" : "hsl(var(--border))";
    return (
      <div
      key={deck.id}
      className="home-deck-card flex flex-col gap-2.5 md:gap-4 p-3.5 md:px-6 md:py-5 rounded-[var(--radius)] shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-neutral-300 dark:hover:border-neutral-600 transition-all duration-200 ease-out cursor-pointer relative overflow-hidden"
      ref={(node) => {
        deckRefs.current[deck.id] = node;
      }}
      data-focused-deck={isHighlighted ? "true" : undefined}
      style={{
        background: "hsl(var(--card-bg))",
        border: `1px solid ${restingBorderColor}`,
        boxShadow: isHighlighted ? "0 0 0 2px hsl(var(--primary) / 0.18), 0 20px 48px rgba(109,90,255,0.12)" : undefined,
        transform: isHighlighted ? "translateY(-2px)" : undefined,
      }}
      onMouseEnter={(e) => {
        if (!isHighlighted) e.currentTarget.style.borderColor = "rgb(212, 212, 212)";
      }}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = restingBorderColor)}
    >
      <div
        className="absolute inset-x-0 top-0 h-[2px] rounded-t-[var(--radius)]"
        style={{ background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))" }}
      />

      {/* Title + metadata — hierarchy: title dominant, metadata quiet */}
      <div className="flex flex-col gap-1.5 md:gap-2">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <div className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-[hsl(var(--primary)/.12)] text-[hsl(var(--primary))] shrink-0">
            <BookOpen size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] md:text-base font-semibold text-[hsl(var(--foreground))] leading-snug truncate">
              {deck.title}
            </h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              {t("sidebar.cardsCount", { count: getDeckCardCount(deck) })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--bg3))] px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            <Globe size={10} className="shrink-0 opacity-80" />
            {deck.lang.toUpperCase()}{deck.level ? ` · ${deck.level}` : ""}
          </span>
          {deck.shareToken && (
            <span className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 border-emerald-500/15 text-emerald-600 dark:bg-emerald-500/10 dark:border-emerald-400/15 dark:text-emerald-300">
              <Link2 size={10} className="shrink-0" />
              {t("home.shared")}
            </span>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {isTeacher ? (
        <div className="flex flex-col gap-2.5 md:gap-4 mt-auto pt-0 md:pt-1">
          {/* Primary row: Study (Fast Track) + Go to Module, or Games */}
          <div className="flex flex-col gap-2 md:gap-3">
            {showLearnButton && getDeckCardCount(deck) > 0 && onStudyDirect && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onStudyDirect(deck.id, deck.type === "tests" ? "test" : getDeckStudyMode()); }}
                  className={btnPrimary}
                >
                  <Play size={16} /> {t(`deckDashboard.start${deck.type === "tests" ? "Test" : (getDeckStudyMode() === "learn" ? "Learn" : getDeckStudyMode() === "test" ? "Test" : "Cards")}`)}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onSelectDeck(deck.id, deck.type === "tests" ? "test" : getDeckStudyMode()); }}
                  className={btnSecondary}
                >
                  <ChevronRight size={16} /> {t("home.goToModule")}
                </button>
              </>
            )}
            {showLearnButton && (!onStudyDirect || getDeckCardCount(deck) === 0) && (
              <button
                type="button"
                onClick={() => onSelectDeck(deck.id, deck.type === "tests" ? "test" : getDeckStudyMode())}
                className={btnPrimary}
              >
                <ChevronRight size={16} /> {t("home.goToModule")}
              </button>
            )}
            {showGamesButton && getResolvedStudyModes(deck).match && (
              <button
                type="button"
                onClick={() => onSelectDeck(deck.id, "games")}
                className={btnGames}
              >
                <Gamepad2 size={16} /> {t("home.play")}
              </button>
            )}
          </div>

          {/* Management row: Edit + Share + Delete — tertiary, aligned */}
          <div className="grid grid-cols-3 gap-1.5 md:gap-2">
            <button
              type="button"
              onClick={() => onEditDeck(deck.id)}
              className={btnSecondary}
            >
              <Pencil size={16} className="shrink-0" /> <span className="truncate">{t("home.edit")}</span>
            </button>
            <button
              type="button"
              onClick={() => handleShare(deck)}
              title={t("home.shareTitleShare")}
              className={deck.shareToken
                ? "inline-flex items-center justify-center gap-2 h-10 md:h-11 px-4 md:px-5 text-sm font-medium rounded-xl md:rounded-2xl transition-all duration-150 border border-emerald-200/80 bg-emerald-50/80 text-emerald-600/90 hover:bg-emerald-100/80 active:scale-[0.98] dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400/90 dark:hover:bg-emerald-500/15"
                : btnSecondary
              }
            >
              {deck.shareToken ? <Link2 size={16} className="shrink-0" /> : <Share2 size={16} className="shrink-0" />}
              <span className="truncate">{t("home.shareTitleShare")}</span>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setDeletingDeck(deck); }}
              className={btnDanger}
            >
              <Trash2 size={16} className="shrink-0" /> <span className="truncate">{t("home.delete")}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 md:gap-4 mt-auto pt-0.5 md:pt-1">
          {showLearnButton && getDeckCardCount(deck) > 0 && onStudyDirect && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onStudyDirect(deck.id, deck.type === "tests" ? "test" : getDeckStudyMode()); }}
                className={btnPrimary}
              >
                <Play size={16} /> {t(`deckDashboard.start${deck.type === "tests" ? "Test" : (getDeckStudyMode() === "learn" ? "Learn" : getDeckStudyMode() === "test" ? "Test" : "Cards")}`)}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onSelectDeck(deck.id, deck.type === "tests" ? "test" : getDeckStudyMode()); }}
                className={btnSecondary}
              >
                <ChevronRight size={16} /> {t("home.goToModule")}
              </button>
            </>
          )}
          {showLearnButton && (!onStudyDirect || getDeckCardCount(deck) === 0) && (
            <button
              type="button"
              onClick={() => onSelectDeck(deck.id, deck.type === "tests" ? "test" : getDeckStudyMode())}
              className={btnPrimary}
            >
              <ChevronRight size={16} /> {t("home.goToModule")}
            </button>
          )}
          {showGamesButton && getResolvedStudyModes(deck).match && (
            <button
              type="button"
              onClick={() => onSelectDeck(deck.id, "games")}
              className={btnGames}
            >
              <Gamepad2 size={16} /> {t("home.play")}
            </button>
          )}
        </div>
      )}
      </div>
  );
  };

  const renderFolderCard = (folder: Folder) => (
    <div
      key={folder.id}
      onClick={() => navigate(`/folder/${folder.id}`)}
      className="rounded-[var(--radius)] shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-lg dark:hover:border-neutral-600 cursor-pointer relative overflow-hidden"
      style={{
        background: "hsl(var(--card-bg))",
        border: "1px solid hsl(var(--border))",
        padding: "1rem 1.25rem",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgb(212, 212, 212)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "hsl(var(--border))")}
    >
      <div
        className="absolute inset-x-0 top-0 h-[2px] rounded-t-[var(--radius)]"
        style={{ background: "linear-gradient(90deg, hsl(var(--accent2)), hsl(var(--primary)))" }}
      />
      <div className="flex items-center gap-2.5 md:gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--primary)/.12)] text-[hsl(var(--primary))]">
          <FolderOpen size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold leading-snug text-[hsl(var(--foreground))]">
            {folder.title}
          </h3>
          {folder.tags && folder.tags.length > 0 && (
            <p className="mt-0.5 truncate text-xs text-[hsl(var(--muted-foreground))]">
              {folder.tags.slice(0, 3).join(" · ")}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-4 md:pt-6 pb-2 overflow-hidden w-full max-w-full min-w-0 space-y-4 md:space-y-6">
      {isTeacher && user && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowShareLibraryModal(true)}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))]/80 hover:border-[hsl(var(--primary))] transition-colors"
          >
            <Share2 size={18} strokeWidth={2} />
            {t("home.share")}
          </button>
        </div>
      )}

      {/* Tabs — chip-style (Notion/Linear) */}
      <nav
        className="flex flex-wrap gap-2 md:gap-2.5"
        aria-label={t("home.libraryTabs")}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`
              h-9 sm:h-10 inline-flex items-center justify-center gap-2 px-3 sm:px-4 text-sm font-medium rounded-xl border transition-all duration-150
              ${activeTab === tab.id
                ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))] shadow-sm active:scale-[0.98]"
                : "bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:bg-[hsl(var(--border))]/10 hover:border-[hsl(var(--border))] active:scale-[0.98]"
              }
            `}
          >
            {tab.icon}
            {t(tab.labelKey)}
          </button>
        ))}
      </nav>

      {/* Controls: Sort + Search (Favorites shows only search) */}
      {showFavorites && user && (
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div className="flex-1 min-w-0 w-full max-w-full flex flex-col items-stretch">
            <span className="text-xs font-medium text-transparent select-none mb-1 ml-1" aria-hidden="true">
              {t("home.filter")}
            </span>
            <div
              className="flex items-center gap-3 py-2.5 px-4 rounded-xl bg-[hsl(var(--bg3))] border border-[hsl(var(--border))]"
            >
              <Search size={20} className="shrink-0 text-[hsl(var(--muted-foreground))]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("home.searchLibraryPlaceholder")}
                className="flex-1 min-w-0 bg-transparent text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none text-sm"
              />
            </div>
          </div>
        </div>
      )}
      {!showFavorites && (totalDecks > 0 || totalFolders > 0) && (
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 mb-6">
          {/* Filter dropdown */}
          <div ref={filterDropdownRef} className="relative flex flex-col items-start shrink-0">
            <span className="text-xs font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] mb-1 ml-1">
              {t("home.filter")}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSortDropdownOpen((o) => !o)}
                className="h-9 sm:h-10 flex items-center gap-2 px-3 sm:px-4 rounded-xl border border-[hsl(var(--border))] text-sm font-medium bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/10 transition-all duration-150 active:scale-[0.98]"
              >
                {getFilterButtonLabel()}
                <ChevronDown size={16} className={`shrink-0 transition-transform duration-200 ${sortDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {sortBy === "recent" && activeTimeFilter === "by_date" && (
                <input
                  ref={dateInputRef}
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="px-3 py-1.5 rounded-xl text-sm bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-200 dark:border-[hsl(var(--border))] text-gray-700 dark:text-[hsl(var(--foreground))] shadow-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                />
              )}
            </div>
            {sortDropdownOpen && (
              <div
                className="absolute top-full left-0 mt-1 py-1 rounded-xl bg-[hsl(var(--card-bg))] border border-[hsl(var(--border))] shadow-lg z-10 min-w-[180px]"
              >
                <div>
                <button
                  type="button"
                  onClick={() => setRecentSubmenuOpen((o) => !o)}
                    className={`w-full text-left px-4 py-2.5 text-sm rounded-full mx-1 transition-colors duration-200 flex items-center justify-between ${sortBy === "recent" ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-100 dark:hover:bg-[hsl(var(--bg3))]"}`}
                  >
                    {t("home.sortRecent")}
                    <ChevronRight size={14} className={`shrink-0 ml-1 transition-transform ${recentSubmenuOpen ? "rotate-90" : ""}`} />
                  </button>
                  {recentSubmenuOpen && (
                    <div className="pl-4 py-1 bg-gray-50 dark:bg-[hsl(var(--bg3))]/50">
                      <button
                        type="button"
                        onClick={() => { setSortBy("recent"); setActiveTimeFilter("all_recent"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setSortDropdownOpen(false); setRecentSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTimeFilter === "all_recent" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.sortRecent")}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSortBy("recent"); setActiveTimeFilter("today"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setSortDropdownOpen(false); setRecentSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTimeFilter === "today" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.groupToday")}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSortBy("recent"); setActiveTimeFilter("yesterday"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setSortDropdownOpen(false); setRecentSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTimeFilter === "yesterday" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.timeFilterYesterday")}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSortBy("recent"); setActiveTimeFilter("this_week"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setSortDropdownOpen(false); setRecentSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTimeFilter === "this_week" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.groupThisWeek")}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSortBy("recent"); setActiveTimeFilter("last_week"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setSortDropdownOpen(false); setRecentSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTimeFilter === "last_week" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.timeFilterLastWeek")}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSortBy("recent"); setActiveTimeFilter("last_month"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setSortDropdownOpen(false); setRecentSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTimeFilter === "last_month" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.timeFilterLastMonth")}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSortBy("recent"); setActiveTimeFilter("by_date"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setSortDropdownOpen(false); setRecentSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTimeFilter === "by_date" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.timeFilterByDate")}
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setSortBy("alphabetical"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setActiveTimeFilter("all_recent"); setSortDropdownOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); setRecentSubmenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm rounded-full mx-1 transition-colors duration-200 ${sortBy === "alphabetical" ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-100 dark:hover:bg-[hsl(var(--bg3))]"}`}
                >
                  {t("home.sortAlphabetical")}
                </button>
                <div>
                  <button
                    type="button"
                    onClick={() => setTagSubmenuOpen((o) => !o)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${sortBy === "tag" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                  >
                    {t("home.sortByTag")}
                    <ChevronRight size={14} className={`shrink-0 ml-1 transition-transform ${tagSubmenuOpen ? "rotate-90" : ""}`} />
                  </button>
                  {tagSubmenuOpen && (
                    <div className="pl-4 py-1 bg-gray-50 dark:bg-[hsl(var(--bg3))]/50">
                      <button
                        type="button"
                        onClick={() => { setSortBy("tag"); setActiveTag(null); setActiveTypeFilter(null); setActiveTimeFilter("all_recent"); setSortDropdownOpen(false); setTagSubmenuOpen(false); setLanguageSubmenuOpen(false); setTypeSubmenuOpen(false); setRecentSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTag === null ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.filterAllTags")}
                      </button>
                      {uniqueTags.map((tag) => (
                        <div
                          key={tag}
                          className="flex justify-between items-center group w-full"
                        >
                          <button
                            type="button"
                            onClick={() => { setSortBy("tag"); setActiveTag(tag); setActiveTypeFilter(null); setActiveTimeFilter("all_recent"); setSortDropdownOpen(false); setTagSubmenuOpen(false); setLanguageSubmenuOpen(false); setTypeSubmenuOpen(false); setRecentSubmenuOpen(false); }}
                            className={`flex-1 text-left px-3 py-2 text-sm transition-colors rounded ${activeTag === tag ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                          >
                            {tag}
                          </button>
                          {onRemoveTag && (
                            <button
                              type="button"
                              onClick={(e) => handleDeleteTag(e, tag)}
                              className="shrink-0 p-1 rounded text-gray-400 hover:text-red-500 dark:text-[hsl(var(--muted-foreground))] dark:hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              title={t("home.deleteTag")}
                              aria-label={t("home.deleteTag")}
                            >
                              <Trash2 size={14} strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setLanguageSubmenuOpen((o) => !o)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${sortBy === "language" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                  >
                    {t("home.sortByLanguage")}
                    <ChevronRight size={14} className={`shrink-0 ml-1 transition-transform ${languageSubmenuOpen ? "rotate-90" : ""}`} />
                  </button>
                  {languageSubmenuOpen && (
                    <div className="pl-4 py-1 bg-gray-50 dark:bg-[hsl(var(--bg3))]/50">
                      <button
                        type="button"
                        onClick={() => { setSortBy("language"); setActiveLanguage(null); setActiveTag(null); setActiveTypeFilter(null); setActiveTimeFilter("all_recent"); setSortDropdownOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); setRecentSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeLanguage === null ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.filterAllLanguages")}
                      </button>
                      {uniqueLanguages.map(({ lang, label }) => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => { setSortBy("language"); setActiveLanguage(lang); setActiveTag(null); setActiveTypeFilter(null); setActiveTimeFilter("all_recent"); setSortDropdownOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setTypeSubmenuOpen(false); setRecentSubmenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeLanguage === lang ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setTypeSubmenuOpen((o) => !o)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${sortBy === "type" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                  >
                    {t("home.sortByType")}
                    <ChevronRight size={14} className={`shrink-0 ml-1 transition-transform ${typeSubmenuOpen ? "rotate-90" : ""}`} />
                  </button>
                  {typeSubmenuOpen && (
                    <div className="pl-4 py-1 bg-gray-50 dark:bg-[hsl(var(--bg3))]/50">
                      <button
                        type="button"
                        onClick={() => { setSortBy("type"); setActiveTypeFilter(null); setActiveLanguage(null); setActiveTag(null); setActiveTimeFilter("all_recent"); setSortDropdownOpen(false); setTypeSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setRecentSubmenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${sortBy === "type" && activeTypeFilter === null ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                      >
                        {t("home.filterAllTypes")}
                      </button>
                      {(["cards", "learn", "test"] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => { setSortBy("type"); setActiveTypeFilter(mode); setActiveLanguage(null); setActiveTag(null); setActiveTimeFilter("all_recent"); setSortDropdownOpen(false); setTypeSubmenuOpen(false); setLanguageSubmenuOpen(false); setTagSubmenuOpen(false); setRecentSubmenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors rounded ${activeTypeFilter === mode ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--bg3))]"}`}
                        >
                          {t(`deckModal.studyMode${mode.charAt(0).toUpperCase() + mode.slice(1)}`)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="flex-1 min-w-0 w-full max-w-full flex flex-col items-stretch">
            <span className="text-xs font-medium text-transparent select-none mb-1 ml-1" aria-hidden="true">
              {t("home.filter")}
            </span>
            <div
              className="flex items-center gap-3 py-2.5 px-4 rounded-xl bg-[hsl(var(--bg3))] border border-[hsl(var(--border))]"
            >
            <Search size={20} className="shrink-0 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("home.searchLibraryPlaceholder")}
              className="flex-1 min-w-0 bg-transparent text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none text-sm"
            />
            </div>
          </div>
        </div>
      )}

      {/* Favorites loading */}
      {showFavorites && favoritesLoading && (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            background: "hsl(var(--card-bg))",
            border: "2px dashed hsl(var(--border))",
            borderRadius: "var(--radius)",
            color: "hsl(var(--muted-foreground))",
          }}
        >
          {t("common.loading")}
        </div>
      )}

      {showLibraryLoading && (
        <div className="space-y-6 md:space-y-8">
          <div
            className="grid gap-4 md:gap-5 w-full max-w-full min-w-0"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(380px, 100%), 1fr))" }}
          >
            {Array.from({ length: showFolders ? 3 : 4 }).map((_, index) => (
              <div
                key={`library-skeleton-${index}`}
                className="relative overflow-hidden rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-3.5 md:px-5 md:py-5 shadow-sm"
              >
                <div className="absolute inset-x-0 top-0 h-[2px] rounded-t-[var(--radius)] bg-[hsl(var(--bg3))]" />
                <div className="flex flex-col gap-1.5 md:gap-2">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className="h-9 w-9 md:h-10 md:w-10 rounded-lg md:rounded-xl bg-[hsl(var(--bg3))] animate-pulse" />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="h-4 w-3/4 rounded-md bg-[hsl(var(--bg3))] animate-pulse" />
                      <div className="h-3 w-16 rounded-md bg-[hsl(var(--bg3))] animate-pulse" />
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="h-5 w-14 rounded-md bg-[hsl(var(--bg3))] animate-pulse" />
                    <div className="h-5 w-16 rounded-md bg-[hsl(var(--bg3))] animate-pulse" />
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-2 md:gap-2.5 pt-0">
                  <div className="h-11 rounded-xl md:rounded-2xl bg-[hsl(var(--bg3))] animate-pulse" />
                  <div className="h-10 rounded-xl md:rounded-2xl bg-[hsl(var(--bg3))] animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!showLibraryLoading && !favoritesLoading && (!hasItems || (showDecks && totalDecks === 0) || (showFolders && totalFolders === 0) || (showFavorites && favoriteCards.length === 0)) && (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            background: "hsl(var(--card-bg))",
            border: "2px dashed hsl(var(--border))",
            borderRadius: "var(--radius)",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>
            {showFolders ? "📁" : showFavorites ? "⭐" : "🎴"}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            {showFavorites ? t("favoritesEmptyTitle") : t("home.noDecks")}
          </div>
          <p style={{ fontSize: 14, color: "hsl(var(--muted-foreground))", marginBottom: 20 }}>
            {showFavorites ? t("favoritesEmptyHint") : t("home.noDecksHint")}
          </p>
          {!showFolders && !showFavorites && (
            <button
              type="button"
              onClick={() => navigate("/library/create")}
              className="flex gap-2 items-center justify-center px-5 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {t("buttons.newSet")}
            </button>
          )}
          {showFolders && onCreateFolder && !showFavorites && (
            <button
              type="button"
              onClick={onCreateFolder}
              className="mt-3 px-4 py-2 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-medium"
            >
              + {t("home.newFolder")}
            </button>
          )}
        </div>
      )}

      {/* No search results */}
      {!showLibraryLoading &&
        hasItems &&
        (searchQuery.trim() &&
          (showDecks ? filteredDecks.length === 0 : showFolders ? filteredFolders.length === 0 : filteredFavoriteCards.length === 0)) && (
          <div
            className="text-center py-12 rounded-xl"
            style={{
              background: "hsl(var(--card-bg))",
              border: "1px solid hsl(var(--border))",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            {t("home.searchNoResults")}
          </div>
        )}

      {/* Favorites tab content */}
      {showFavorites && !favoritesLoading && filteredFavoriteCards.length > 0 && (
        <div className="space-y-4">
          <section>
            <div className="flex flex-col gap-3 w-full max-w-full min-w-0">
              {filteredFavoriteCards.map((fc) => (
                <div
                  key={`${fc.deckId}-${fc.card.id ?? fc.card.front}`}
                  style={{
                    background: "hsl(var(--card-bg))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "var(--radius)",
                    padding: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">
                        {fc.deckEmoji} {fc.deckTitle}
                      </div>
                      <div className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">
                        {fc.card.front}
                      </div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">
                        {fc.card.back}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => onSelectDeck(fc.deckId, "cards")}
                        className={btnPrimary}
                      >
                        <GraduationCap size={14} /> {t("home.learn")}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!user?.id || !fc.card.id) return;
                          try {
                            await removeCardFavorite(user.id, fc.card.id);
                            loadFavorites();
                          } catch (e) {
                            console.warn("[Favorites] Failed to remove:", e);
                          }
                        }}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/20 transition-colors"
                        title={t("remove_favorite")}
                      >
                        <Star size={14} className="fill-current" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Time-grouped content — incremental: first N items, then "Load more". Mobile: max 2 until expanded. */}
      {!showLibraryLoading && !showFavorites && hasItems && !(searchQuery.trim() && (showDecks ? filteredDecks.length === 0 : filteredFolders.length === 0)) && (() => {
        type FlatItem = { type: "group"; key: TimeGroup } | { type: "deck"; deck: Deck } | { type: "folder"; folder: Folder };
        const flatList: FlatItem[] = [];
        for (const groupKey of groupOrder) {
          const deckItems = showDecks ? groupedDecks[groupKey] : [];
          const folderItems = showFolders ? groupedFolders[groupKey] : [];
          const items = showDecks ? deckItems : folderItems;
          if (items.length > 0) {
            flatList.push({ type: "group", key: groupKey });
            for (const deck of deckItems) flatList.push({ type: "deck", deck });
            for (const folder of folderItems) flatList.push({ type: "folder", folder });
          }
        }
        const visibleFlat = flatList.slice(0, visibleLibraryCount);
        const hasMore = visibleFlat.length < flatList.length;
        const totalItems = flatList.length;
        const visibleSections: { groupKey: TimeGroup; deckItems: Deck[]; folderItems: Folder[] }[] = [];
        let currentGroup: TimeGroup | null = null;
        let currentDecks: Deck[] = [];
        let currentFolders: Folder[] = [];
        for (const item of visibleFlat) {
          if (item.type === "group") {
            if (currentGroup) visibleSections.push({ groupKey: currentGroup, deckItems: currentDecks, folderItems: currentFolders });
            currentGroup = item.key;
            currentDecks = [];
            currentFolders = [];
          } else if (item.type === "deck") currentDecks.push(item.deck);
          else currentFolders.push(item.folder);
        }
        if (currentGroup) visibleSections.push({ groupKey: currentGroup, deckItems: currentDecks, folderItems: currentFolders });

        return (
          <div className="space-y-8">
            {visibleSections.map(({ groupKey, deckItems, folderItems }, sectionIndex) => {
              const startIndex = visibleSections.slice(0, sectionIndex).reduce((sum, s) => sum + (showDecks ? s.deckItems.length : s.folderItems.length), 0);
              const sectionFullyHidden = !isExpanded && startIndex >= 2;

              return (
                <section
                  key={groupKey}
                  className={sectionFullyHidden ? "hidden md:block first:[&_h3]:mt-0" : "first:[&_h3]:mt-0"}
                >
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 mt-8">
                    {groupLabels[groupKey]}
                  </h3>
                  <div
                    className="grid gap-5 w-full max-w-full min-w-0"
                    style={{
                      gridTemplateColumns: "repeat(auto-fill, minmax(min(380px, 100%), 1fr))",
                    }}
                  >
                    {showDecks && deckItems.map((deck, index) => {
                      const globalIndex = startIndex + 1 + index;
                      const itemHidden = !isExpanded && globalIndex >= 2;
                      const showAnimation = isExpanded && globalIndex >= 2 && totalItems > 2;
                      return (
                        <div
                          key={deck.id}
                          className={
                            itemHidden
                              ? "hidden md:block"
                              : showAnimation
                                ? "block animate-in slide-in-from-top-2 fade-in duration-300"
                                : "block"
                          }
                        >
                          {renderDeckCard(deck)}
                        </div>
                      );
                    })}
                    {showFolders && folderItems.map((folder, index) => {
                      const globalIndex = startIndex + 1 + index;
                      const itemHidden = !isExpanded && globalIndex >= 2;
                      const showAnimation = isExpanded && globalIndex >= 2 && totalItems > 2;
                      return (
                        <div
                          key={folder.id}
                          className={
                            itemHidden
                              ? "hidden md:block"
                              : showAnimation
                                ? "block animate-in slide-in-from-top-2 fade-in duration-300"
                                : "block"
                          }
                        >
                          {renderFolderCard(folder)}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}

            {/* Auto-scroll sentinel: when visible, load more */}
            {hasMore && <div ref={loadMoreSentinelRef} className="h-1 w-full min-h-[1px]" aria-hidden="true" />}
            {/* Load more (incremental) — desktop and mobile */}
            {hasMore && (
              <button
                type="button"
                onClick={() => setVisibleLibraryCount((c) => c + LIBRARY_PAGE_SIZE)}
                className="w-full mt-4 py-3 px-4 rounded-xl text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))]/80 transition-transform active:scale-95"
              >
                {t("common.showMore")}
              </button>
            )}
            {/* Mobile: collapse/expand first 2 */}
            {totalItems > 2 && (
              <button
                type="button"
                onClick={() => setIsExpanded((e) => !e)}
                className="block md:hidden w-full mt-4 py-3 px-4 rounded-xl text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))]/80 transition-transform active:scale-95"
              >
                {isExpanded ? t("common.showLess") : t("common.showMore")}
              </button>
            )}

            {/* Add new deck card (any deck tab) */}
          {activeTab !== "folders" && activeTab !== "favorites" && (
            <section>
              <div
                className="w-full max-w-full min-w-0"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(min(380px, 100%), 1fr))",
                  gap: 20,
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/library/create")}
                  style={{
                    background: "transparent",
                    border: "2px dashed hsl(var(--border))",
                    borderRadius: "var(--radius)",
                    padding: 20,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    minHeight: 140,
                    transition: "all .2s",
                    color: "hsl(var(--muted-foreground))",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "hsl(var(--primary))";
                    e.currentTarget.style.background = "rgba(109,90,255,.05)";
                    e.currentTarget.style.color = "hsl(var(--primary))";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "hsl(var(--border))";
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "hsl(var(--muted-foreground))";
                  }}
                >
                  <span style={{ fontSize: 32 }}>+</span>
                  <span className="text-base md:text-lg font-semibold">
                    {t("sidebar.newDeckTitle")}
                  </span>
                </button>
              </div>
            </section>
          )}
        </div>
        );
      })()}

      {deletingDeck && (
        <DeleteDeckModal
          deck={deletingDeck}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeletingDeck(null)}
        />
      )}
      {sharingDeck && (
        <ShareModal
          deck={sharingDeck}
          onClose={() => setSharingDeck(null)}
          onShared={(deckId, token, shareAccess) => {
            onDeckShared?.(deckId, token, shareAccess);
          }}
        />
      )}
      {showShareLibraryModal && user && (
        <ShareLibraryModal
          userId={user.id}
          onClose={() => setShowShareLibraryModal(false)}
        />
      )}

      {deleteTagConfirm && (
        <AlertDialog open={!!deleteTagConfirm} onOpenChange={(open) => !open && setDeleteTagConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("home.deleteTagConfirm")}</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("folder.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  await handleConfirmDeleteTag();
                }}
              >
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
